import React from 'react';
import SubPage from '../components/site/SubPage';
import { PageSection, PageTitleBox, SectionHeading } from '../components/site/SubPageParts';

const IMG = '/images/site/emotional-manipulation';

const CAROLYN_LINES = [
  'I hear you, honey. But Jerry is such a nice man! He reminds me of Uncle Ced — easy to talk with, we’ve got a real connection.',
  'I miss your dad so much. It’s not the money — that’s important, it’s more than that. It’s being alone. You have your family.',
  'Money is tight ever since dad passed; he’s talked about bitcoin. He’s in some type of international business I think — he knows his stuff, he’s offered to help me.',
  'Lisa, I know you’re worried about me, it’s sweet, but I’m fine. Nothing to worry about — I’m fine.',
];

const LISA_LINES = [
  'Oh, my word Mom — do you hear yourself? It’s a lie, a story, it’s not true. Jerry, or whatever his name is, wants your money. He’s a scammer. And he’s going to hurt you. How did you meet this guy Jerry — on one of those dating sites?',
  'Mom, you don’t have a lot of money for retirement — he’s going to steal what little you have. For the love of God, stop. Once the money is gone, you can kiss those morning coffee chats or afternoon calls goodbye — they’ll stop.',
  'It’s a lie. When he has finished taking everything, it’s going to be me left holding the bag, trying to put you back together.',
];

const SCAMMER_LINES = [
  'This is like taking candy from a baby — it’s almost too easy.',
  'We target middle age to senior adult folks. Widows, widowers or single folks looking for Mr., Mrs. or Miss Amazing — connect with them, tell them what they want to hear. That’s the ticket. They’re all loaded, online, and ready to find their soulmate.',
];

const EMOTIONAL_IMPACT = ['Emotional pain and despair', 'Self-doubt', 'Embarrassment'];
const FINANCIAL_IMPACT = [
  'Cheated out of $250,000',
  'Participated in money laundering.',
  'Isolated and estranged from her family and friends.',
  'Bank account is $0.',
  'No more calls, text or emails.',
];

const Quote = ({ children }) => (
  <p className="text-[15px] leading-snug text-black md:text-[17px]">{children}</p>
);

/** ↘ prefixed impact item. */
const ImpactItem = ({ children }) => (
  <li className="flex items-start gap-3 text-[15px] text-black md:text-[17px]">
    <span aria-hidden="true">&#8600;</span>
    <span>{children}</span>
  </li>
);

const EmotionalManipulation = () => (
  <SubPage title="Emotional Manipulation">
    <PageTitleBox>The Con that Always was.</PageTitleBox>

    {/* ---------------- Hero ---------------- */}
    {/* 9-10 (slide 33): the photo carries no overlaid copy. Removing it also
        retires the darkened grey that existed only to make that text clear
        3:1 against this greyscale frame. */}
    <PageSection className="pt-4">
      <img
        src={`${IMG}/hero-soulmate.jpeg`}
        alt="A woman looking directly at the camera, in black and white"
        className="h-[280px] w-full object-cover object-right small:aspect-[168/65] small:h-auto"
      />
    </PageSection>

    {/* ---------------- Psychology of reasoning ---------------- */}
    <PageSection className="pt-8">
      <SectionHeading>Psychology of Reasoning.</SectionHeading>
      <div className="mt-4 grid grid-cols-1 items-stretch bg-black lg:grid-cols-[minmax(0,1fr)_minmax(0,32%)]">
        <div className="space-y-4 px-6 py-8 text-[15px] leading-relaxed text-white md:px-10 md:text-[17px]">
          <p>
            These beasts of prey understand the &ldquo;psychology of reasoning&rdquo; and how to
            deal with and overcome their victims&rsquo; objections. Put another way &mdash; they
            know what makes their victims tick.
          </p>
          <p>
            They are Apex predators, working together in packs; they&rsquo;re cunning and
            ruthless, encircling their prey and devouring them. The current digital landscape for
            all users, but especially seniors, is like lambs &mdash; loved ones being led to the
            slaughter as these online Fraudsters, Cyberthieves and Digital Predators stalk them
            for SPORT and MONEY!
          </p>
        </div>
        <img
          src={`${IMG}/psychology-brain.jpeg`}
          alt="A glowing network in the shape of a brain"
          className="h-full min-h-[180px] mini:h-[390px] w-full object-cover"
        />
      </div>
    </PageSection>

    {/* ---------------- Story of Carolyn ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>Story of Carolyn.</SectionHeading>

      <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12">
        <img
          src={`${IMG}/carolyn.jpeg`}
          alt="Carolyn, mid-conversation on a sofa"
          className="aspect-[5/4] w-full object-cover object-top"
        />
        <div className="space-y-4">
          {CAROLYN_LINES.map((line) => (
            <Quote key={line}>{line}</Quote>
          ))}
        </div>
      </div>

      <h3 className="mt-10 text-[22px] font-bold text-black md:text-[30px]">Lisa &ndash; Victim.</h3>
      <div className="mt-4 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12">
        <div className="space-y-4 md:order-first">
          {LISA_LINES.map((line) => (
            <Quote key={line}>{line}</Quote>
          ))}
        </div>
        <img
          src={`${IMG}/lisa.jpeg`}
          alt="Lisa, pleading with her mother"
          className="aspect-[5/4] w-full object-cover object-top"
        />
      </div>
    </PageSection>

    {/* ---------------- Scammer ---------------- */}
    <PageSection className="pt-12">
      <h3 className="text-[22px] font-bold text-black md:text-[30px]">Scammer.</h3>
      {/* Sized to the mockup's proportions: a taller plate (~4:1 rather than
          the 6.7:1 it had), larger dialogue type, and a wider image column.
          object-left because the source is a 3.2:1 banner with the figure in
          its left third -- a centred crop lands on the empty black half. */}
      <div className="mt-5 grid grid-cols-1 items-stretch bg-black lg:min-h-[320px] lg:grid-cols-[minmax(0,1fr)_minmax(0,42%)]">
        <div className="space-y-5 px-6 py-8 text-[17px] leading-relaxed text-white md:flex md:flex-col md:justify-center md:px-10 md:py-10 md:text-[22px]">
          {SCAMMER_LINES.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
        <img
          src={`${IMG}/scammer.jpeg`}
          alt="A masked figure in a hoodie at a laptop"
          className="h-full min-h-[200px] mini:h-[385px] w-full object-cover object-left"
        />
      </div>
    </PageSection>

    {/* ---------------- Aftermath ---------------- */}
    <PageSection className="pt-12">
      <div className="border border-black/25 p-4 md:p-6">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,42%)_minmax(0,1fr)] md:gap-12">
          <img
            src={`${IMG}/aftermath.jpeg`}
            alt="Carolyn and Lisa sitting apart on a sofa, in black and white"
            className="h-[280px] w-full object-cover small:aspect-[181/140] small:h-auto"
          />
          <div className="md:pt-4">
            <p className="text-[17px] text-black md:text-[20px]">Emotional Impact</p>
            <ul className="mt-2 space-y-1">
              {EMOTIONAL_IMPACT.map((item) => (
                <ImpactItem key={item}>{item}</ImpactItem>
              ))}
            </ul>

            <p className="mt-6 text-[17px] text-black md:text-[20px]">Financial</p>
            <ul className="mt-2 space-y-1">
              {FINANCIAL_IMPACT.map((item) => (
                <ImpactItem key={item}>{item}</ImpactItem>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </PageSection>

    {/* ---------------- MoSAIC PSYOPS ---------------- */}
    {/* 9-10, per the supplied screenshot: the heading alone, centred in the
        plate, with a gap between the plate and the photo. The arrow that
        linked to the Abuse Playbook is gone -- the nav and the homepage card
        both still reach that page. */}
    <PageSection className="pt-12">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-8">
        <div className="flex flex-col items-center justify-center bg-black px-6 py-10 md:px-12">
          <h3 className="text-center text-[26px] font-bold leading-tight text-white md:text-[40px]">
            MoSAIC PSYOPS &mdash; Spot Grooming Behaviors Every Adult Should Recognize.
          </h3>
        </div>
        <img
          src={`${IMG}/mosaic-psyops.jpeg`}
          alt="A woman at a laptop with her hands pressed together"
          className="h-[280px] w-full object-cover small:aspect-[656/437] small:h-auto lg:aspect-auto lg:h-auto"
        />
      </div>
    </PageSection>
  </SubPage>
);

export default EmotionalManipulation;
